/** Engine-independent swept ballistics. Coordinates belong to the caller's
 * stable frame (planet-local in world play, Y-up in races). */
import {segmentSphere} from './hit.js';
import {blastDamage,directDamage,type WeaponStats} from './weapons.js';
export type ShotVector=[number,number,number];
export interface ShotTarget {key:string;position:ShotVector;radius:number}
export interface ShotWorld {
  gravity(at:ShotVector):ShotVector;
  targets():readonly ShotTarget[];
  /** Earliest solid intersection in [0,1], or null. Also used for blast cover. */
  solid(from:ShotVector,to:ShotVector):number|null;
}
export interface ShotContact {target:ShotTarget;point:ShotVector;damage:number;explosive:boolean}
export interface ShotStep {contacts:ShotContact[];stop:ShotVector|null}
export interface BallisticShot {position:ShotVector;velocity:ShotVector;distance:number;age:number;hit:Set<string>;stopped:boolean;stats:WeaponStats}
export function createShot(position:ShotVector,direction:ShotVector,stats:WeaponStats):BallisticShot {
  const n=Math.hypot(...direction)||1;
  return {position:[...position],velocity:direction.map(v=>v/n*stats.speed) as ShotVector,distance:0,age:0,hit:new Set(),stopped:false,stats};
}
const lerp=(a:ShotVector,b:ShotVector,t:number)=>a.map((v,i)=>v+(b[i]-v)*t) as ShotVector;
export function stepShot(shot:BallisticShot,dt:number,world:ShotWorld):ShotStep {
  const contacts:ShotContact[]=[];let stop:ShotVector|null=null;
  const w=shot.stats;
  const finish=()=>{
    shot.stopped=true;stop=[...shot.position];
    if(w.explosiveDamage<=0||w.explosiveRadius<=0)return;
    for(const target of world.targets()){
      const distance=Math.hypot(...target.position.map((v,i)=>v-shot.position[i]));
      const damage=blastDamage(w,distance);if(damage<=0)continue;
      // Back off the stopping surface a millimetre so the solid just struck
      // does not occlude the near side of its own blast.
      const speed=Math.hypot(...shot.velocity)||1;
      const from=shot.position.map((v,i)=>v-shot.velocity[i]/speed*.002) as ShotVector;
      const block=world.solid(from,target.position);
      if(block!==null&&block<.999)continue;
      contacts.push({target,point:[...target.position],damage,explosive:true});
    }
  };
  // Fixed maximum substep bounds curved trajectories even on long server ticks.
  for(let left=Math.max(0,dt);left>1e-9&&!shot.stopped;){
    const h=Math.min(left,1/120);left-=h;shot.age+=h;
    const from:ShotVector=[...shot.position],g=w.gravityScale>0?world.gravity(from):[0,0,0];
    let to=from.map((v,i)=>v+shot.velocity[i]*h+.5*g[i]*w.gravityScale*h*h) as ShotVector;
    shot.velocity=shot.velocity.map((v,i)=>v+g[i]*w.gravityScale*h) as ShotVector;
    let length=Math.hypot(...to.map((v,i)=>v-from[i]));
    const remaining=Math.max(0,w.range-shot.distance),expired=length>=remaining||shot.age>=30;
    if(length>remaining){to=lerp(from,to,remaining/length);length=remaining;}
    const solid=world.solid(from,to);
    const hits=world.targets().filter(t=>!shot.hit.has(t.key)).map(target=>({target,t:segmentSphere(...from,...to,...target.position,target.radius)}))
      .filter((v):v is {target:ShotTarget;t:number}=>v.t!==null&&v.t>=0&&v.t<=1&&(solid===null||v.t<solid)).sort((a,b)=>a.t-b.t||a.target.key.localeCompare(b.target.key));
    let end=solid??1;
    for(const hit of hits){
      const point=lerp(from,to,hit.t);shot.hit.add(hit.target.key);
      contacts.push({target:hit.target,point,damage:directDamage(w,shot.distance+length*hit.t),explosive:false});
      if(shot.hit.size>w.pierce){end=hit.t;shot.position=point;shot.distance+=length*end;finish();break;}
    }
    if(shot.stopped)break;
    shot.position=lerp(from,to,end);shot.distance+=length*end;
    if(solid!==null||expired)finish();
  }
  return {contacts,stop};
}
